import * as React from 'react';
import {cloneDeep, get} from 'lodash';
import {Loader, Pagination, SortDirection, TableData, TableHeader, Tabs} from '@flexe/ui-components';
import {CookiesProvider} from 'react-cookie';
import WarehouseSelector from '../../shared/WarehouseSelector';
import OutboundManifestService from '../../shared/services/OutboundManifestService';
import {Warehouse} from '../../shared/CommonInterfaces';
import FlexeContext, {FlexeContextValues} from '../../contexts/FlexeContext';
import OutboundFreightLoadService from '../../shared/services/OutboundFreightLoadService';
import {FreightLoad} from '../outbound-freight/FreightInterfaces';
import formatTimeWithTimezone from '../../shared/utilities/formatTimeWithTimezone';
import WarehouseService from '../../shared/services/WarehouseService';
import Table from './StopgapTableDoNotUse';
import {OutboundTrailerManifest} from './ManifestInterfaces';

interface ManifestIndexProps {
  selectedWarehouse: Warehouse;
  activeWarehouses: Warehouse[];
  inactiveWarehouses?: Warehouse[];
  outboundManifestService?: OutboundManifestService;
  parcelTrailerLoadingEnabled: boolean;
  freightTrailerLoadingEnabled: boolean;
}

interface ManifestIndexState {
  manifests: OutboundTrailerManifest[];
  columnHeaders: ReturnType<typeof COLUMN_HEADERS>;
  continuationTokens: string[];
  currentPage: number;
  totalCount: number;
  selectedWarehouse: Warehouse;
  selectedTab: 'open' | 'completed' | 'cancelled';
  loading: boolean;
  sortColumnIndex: number;
  sortDirection: SortDirection;
  loadMap?: Map<number, FreightLoad>;
  errors?: string[];
}

//The value of each uiIndex has to be in sync with the key in
//TrailerLoading::OutboundManifestQuery#get_column_name
const COLUMN_HEADERS = () => [
  {
    uiIndex: 0,
    element: 'Load ID',
    className: 'id-header'
  },
  {
    uiIndex: 1,
    element: 'Trailer ID'
  },
  {
    uiIndex: 2,
    element: 'Door Location'
  },
  {
    uiIndex: 3,
    element: 'SCAC'
  },
  {
    uiIndex: 4,
    element: 'Created At'
  }
];

const STARTING_SORT_INDEX = 4;
const LOADS_UI_FF = 'p2d_enable_new_loads_ui';

class ManifestIndex extends React.Component<ManifestIndexProps, ManifestIndexState> {
  public static contextType = FlexeContext;
  private manifestService: OutboundManifestService;
  private freightLoadService: OutboundFreightLoadService;
  private warehouseService: WarehouseService;
  private freightTrailerLoadingEnabled: boolean;

  constructor(props: ManifestIndexProps, context: FlexeContextValues) {
    super(props);

    this.state = {
      manifests: [],
      columnHeaders: COLUMN_HEADERS(),
      continuationTokens: [],
      currentPage: 1,
      totalCount: 0,
      selectedTab: 'open',
      selectedWarehouse: this.props.selectedWarehouse,
      loading: false,
      errors: null,
      sortColumnIndex: COLUMN_HEADERS().find((header) => header.uiIndex === STARTING_SORT_INDEX).uiIndex,
      sortDirection: SortDirection.Ascending
    };

    if (!props.parcelTrailerLoadingEnabled) {
      // Replace Door Location header with the previous data when the feature is off
      this.state.columnHeaders[2].element = 'Loading Location';
      const newHeaders = cloneDeep(this.state.columnHeaders);
      newHeaders[2].element = 'Loading Location';
      this.setState({columnHeaders: newHeaders});
    }

    this.freightTrailerLoadingEnabled = props.freightTrailerLoadingEnabled;
    this.manifestService = this.props.outboundManifestService || new OutboundManifestService(context.authenticityToken);
    this.freightLoadService = new OutboundFreightLoadService(context.authenticityToken);
    this.warehouseService = new WarehouseService(context.authenticityToken);
  }

  public componentDidMount() {
    this.loadManifests();
  }

  public render() {
    const tableData = this.getTableData();
    const sortables: number[] = this.state.columnHeaders
      .filter((header) => Number.isInteger(header.uiIndex))
      .map((header) => header.uiIndex);
    return (
      <div className="outbound-manifest-component container-fluid">
        {this.state.errors && (
          <div className="alert alert-danger" role="alert">
            {this.state.errors.map((err, idx) => (
              <span key={idx}>{err}</span>
            ))}
          </div>
        )}
        <div className="row">
          <div className="col-sm-6">
            <h1>Loads</h1>
          </div>
        </div>
        <h4>
          <CookiesProvider>
            <WarehouseSelector
              selectedWarehouse={this.state.selectedWarehouse}
              activeWarehouses={this.props.activeWarehouses}
              inactiveWarehouses={this.props.inactiveWarehouses}
              onSelect={this.handleWarehouseSelected}
            />
          </CookiesProvider>
        </h4>

        <Tabs tabs={this.getTabs()} tabEvent={this.handleTabEvent} />
        <div className="space-above">
          <Loader loading={this.state.loading} />
          {!this.state.loading && tableData.rows.length === 0 && (
            <p className="col-sm-12">No outbound trailer loads matching the current selection.</p>
          )}
          {!this.state.loading && tableData.rows.length > 0 && (
            <Table
              tableClass="table-striped"
              tableData={tableData}
              sortConfig={{
                sortDirection: this.state.sortDirection,
                sortColumnIndex: this.state.sortColumnIndex,
                sortableHeaders: sortables
              }}
              onSort={this.handleSort}
            />
          )}
        </div>
      </div>
    );
  }

  private getTabs() {
    return [
      {
        key: 'open',
        title: '',
        subTitle: 'In Progress',
        active: this.state.selectedTab === 'open'
      },
      {
        key: 'completed',
        title: '',
        subTitle: 'Completed',
        active: this.state.selectedTab === 'completed',
        pullRight: true
      },
      {
        key: 'cancelled',
        title: '',
        subTitle: 'Cancelled',
        active: this.state.selectedTab === 'cancelled',
        pullRight: true
      }
    ];
  }

  private loadManifests = async () => {
    this.setState({loading: true});

    let sortIndex = this.state.sortColumnIndex;
    if (!this.props.parcelTrailerLoadingEnabled && sortIndex === 2) {
      // Use a special index for when the prop is off so 2 values can "share" a column
      sortIndex = -1;
    }

    const listQuery = {
      statuses: [this.state.selectedTab],
      warehouseIds: [this.state.selectedWarehouse.id],
      sortIndex,
      sortDirection: this.state.sortDirection
    };

    const {currentPage, continuationTokens} = this.state;
    const continuationToken = currentPage > 1 ? continuationTokens[currentPage - 2] : null;
    const response = await this.manifestService.listOutboundManifests(listQuery, 50, continuationToken);
    const errors = get(response, 'errors') || [];
    const data = get(response, 'data');
    if (errors.length > 0) {
      this.setState({
        errors: this.manifestService.processErrors(errors)
      });
    } else {
      this.setState((prevState) => {
        const nextPageToken = data.continuationToken;
        const newContinuationTokens = prevState.continuationTokens;

        if (nextPageToken && prevState.currentPage > newContinuationTokens.length) {
          newContinuationTokens.push(nextPageToken);
        }

        return {
          continuationTokens: newContinuationTokens,
          totalCount: data.totalMatchingResults,
          manifests: data.outboundManifests
        };
      });
    }

    this.loadFreightLoads();
    this.setState({loading: false});
  };

  private loadFreightLoads = async () => {
    const loadValues = this.state.manifests.map((val) => {
      return val.freightLoadId;
    });
    const loadIds = loadValues.filter((id) => {
      return id !== null;
    });

    if (loadIds.length <= 0) {
      return;
    }

    const response = await this.freightLoadService.getFreightLoads(loadIds);
    const errors = get(response, 'errors') || [];
    const data = get(response, 'data');
    if (errors.length > 0) {
      this.setState({
        loadMap: null,
        errors: this.manifestService.processErrors(errors)
      });
    } else {
      const loadMap = new Map<number, FreightLoad>();
      data?.freightLoads.forEach((load) => {
        loadMap.set(load.id, load);
      });
      this.setState({
        loadMap
      });
    }

    this.setState({loading: false});
  };

  private getTableData(): TableData {
    const tableData = {
      headers: this.buildHeaders(),
      rows: this.buildRows()
    };
    return tableData;
  }

  private buildHeaders(): TableHeader[] {
    let headers = [];
    headers = this.state.columnHeaders.map((header) => ({
      element: header.element,
      className: header.className
    }));
    if (this.freightTrailerLoadingEnabled) {
      headers.push({
        element: 'Destination ID',
        className: ''
      });
    }
    headers.push({
      className: 'pagination-header',
      element: (
        <Pagination
          page={this.state.currentPage}
          pageSize={50}
          paginationHandler={this.handlePagination}
          totalCount={this.state.totalCount}
        />
      )
    });
    return headers;
  }

  private buildRows() {
    if (!this.state.manifests || this.state.manifests.length <= 0) {
      return [];
    }

    const rows = this.state.manifests.map((m: OutboundTrailerManifest) => {
      const row: (string | React.ReactElement | number)[] = [
        m.id,
        m.trailerId,
        m.doorLocation,
        m.scacCode,
        formatTimeWithTimezone(this.state.selectedWarehouse.timezone, m.createdAt)
      ];
      if (this.freightTrailerLoadingEnabled) {
        row.push(
          this.state.loadMap?.has(m.freightLoadId)
            ? this.state.loadMap?.get(m.freightLoadId).loadStops[0].destinationTag
            : '-'
        );
      }

      row.push(
        <a
          className="fa fa-chevron-right"
          style={{float: 'right'}}
          title={'click to view this load'}
          href={`/wh/outbound_manifests/${m.id}`}
        ></a>
      );

      if (!this.props.parcelTrailerLoadingEnabled) {
        // Replace the doorLocation column with loading location when the feature is off.
        row[2] = m.loadingLocation;
      }

      return row;
    });
    return rows;
  }

  private handleWarehouseSelected = async (selectedWarehouse: Warehouse) => {
    const ffState = await this.warehouseService.getFeatureFlag(LOADS_UI_FF, selectedWarehouse.id);
    const isLoadManagementEnabled = ffState.data.value === true;

    if (isLoadManagementEnabled) {
      window.open('../wh/loads', '_self');
    } else {
      this.setState({selectedWarehouse, currentPage: 1, continuationTokens: []}, this.loadManifests);
    }
  };

  private handleSort = (colIndex: number, sortDir: SortDirection) => {
    let newSortDir = SortDirection.Ascending;
    newSortDir = sortDir === SortDirection.Ascending ? SortDirection.Descending : SortDirection.Ascending;
    this.setState(
      {
        currentPage: 1,
        continuationTokens: [],
        sortColumnIndex: colIndex,
        sortDirection: newSortDir
      },
      this.loadManifests
    );
  };

  private handleTabEvent = (tabKey) => {
    let sortDir = SortDirection.Ascending;
    const sortIndex = this.state.columnHeaders.find((header) => header.uiIndex === STARTING_SORT_INDEX).uiIndex;
    if (tabKey === 'completed') {
      sortDir = SortDirection.Descending;
    }

    this.setState(
      {
        selectedTab: tabKey,
        currentPage: 1,
        continuationTokens: [],
        sortColumnIndex: sortIndex,
        sortDirection: sortDir
      },
      this.loadManifests
    );
  };

  private handlePagination = (currentPage: number) => {
    this.setState({currentPage}, this.loadManifests);
  };
}

export default ManifestIndex;
